/**
 * Carte « cover » d'un défi live (design Live School, migration 0658) : la
 * photo de la cover en fond, un voile bleu nuit, la pastille d'état (LIVE,
 * DÉFI PRIVÉ, À VENIR, TERMINÉ), le niveau, un grand titre, puis le contenu
 * propre à chaque carte (participants, départ, organisateur, bouton).
 *
 * Toujours en texte blanc sur la photo voilée : lisible en mode clair comme
 * en mode sombre. Couleurs de la marque : bleu #0566f2, or #f4c64e, bleu nuit
 * #061433.
 */

import type { ComponentType, ReactNode } from 'react';
import { cn } from '@jeraisonne/ui';
import type { LiveCardBadge, LiveCardCtaTone } from './live-card-model';

const BADGE_LABEL: Record<LiveCardBadge, string> = {
  live: 'Live',
  private: 'Défi privé',
  upcoming: 'À venir',
  finished: 'Terminé',
};

const BADGE_CLASS: Record<LiveCardBadge, string> = {
  live: 'bg-[#ef4444] text-white shadow-[0_4px_14px_rgba(239,68,68,0.45)]',
  private: 'bg-[#0566f2] text-white shadow-[0_4px_14px_rgba(5,102,242,0.45)]',
  upcoming: 'bg-[#f4c64e] text-[#0a2e57]',
  finished: 'bg-black/35 text-white ring-1 ring-[rgba(255,255,255,0.25)] backdrop-blur',
};

export function LiveBadge({ kind, className }: { kind: LiveCardBadge; className?: string }) {
  return (
    <span
      className={cn(
        'font-display inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-extrabold uppercase tracking-wider',
        BADGE_CLASS[kind],
        className,
      )}
    >
      {kind === 'live' && (
        <span aria-hidden className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75 motion-reduce:animate-none" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
        </span>
      )}
      {BADGE_LABEL[kind]}
    </span>
  );
}

/** Pastille de verre posée sur une photo (niveau, date…). */
export function GlassChip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full bg-black/30 px-2.5 py-1 text-xs font-semibold text-white ring-1 ring-[rgba(255,255,255,0.2)] backdrop-blur',
        className,
      )}
    >
      {children}
    </span>
  );
}

const CTA_TONE: Record<LiveCardCtaTone, string> = {
  primary:
    'bg-linear-to-b from-[#2f86ff] to-[#0566f2] text-white shadow-[0_8px_22px_rgba(5,102,242,0.45)]',
  gold: 'bg-[#f4c64e] text-[#0a2e57] shadow-[0_8px_22px_rgba(244,198,78,0.35)]',
  light: 'bg-[#ffffff] text-[#0a2e57] shadow-[0_8px_22px_rgba(0,0,0,0.25)]',
  glass:
    'bg-[rgba(255,255,255,0.15)] text-white ring-1 ring-[rgba(255,255,255,0.25)] backdrop-blur',
};

/** Classes d'un bouton d'action (lien, bouton, `<Link>`). */
export function liveCtaClass(tone: LiveCardCtaTone, className?: string): string {
  return cn(
    'font-display inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full px-5 text-base font-bold transition active:scale-[0.98] disabled:opacity-50',
    CTA_TONE[tone],
    className,
  );
}

export interface LiveCardMetaItem {
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  text: string;
}

/** Ligne « 👥 54 participants · ⏱ Démarre dans 3 min ». */
export function LiveCardMeta({
  items,
  className,
}: {
  items: LiveCardMetaItem[];
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-1 text-sm font-medium text-white/85',
        className,
      )}
    >
      {items.map(({ icon: Icon, text }) => (
        <span key={text} className="inline-flex items-center gap-1.5">
          <Icon className="h-4 w-4 shrink-0" aria-hidden />
          {text}
        </span>
      ))}
    </div>
  );
}

export function LiveCoverCard({
  coverUrl,
  badge,
  chip,
  corner,
  title,
  size = 'lg',
  className,
  testId,
  children,
}: {
  coverUrl: string;
  badge?: LiveCardBadge | null;
  /** « 3ème · Maths ». */
  chip?: ReactNode;
  /** Coin haut droit (date relative…). */
  corner?: ReactNode;
  title: ReactNode;
  /** `lg` : carte du Feed (grande) ; `md` : listes. */
  size?: 'lg' | 'md';
  className?: string;
  testId?: string;
  children?: ReactNode;
}) {
  return (
    <article
      data-testid={testId}
      className={cn(
        'relative isolate overflow-hidden rounded-3xl bg-[#061433] text-white shadow-[0_14px_34px_rgba(4,14,39,0.28)]',
        className,
      )}
    >
      <img
        src={coverUrl}
        alt=""
        loading="lazy"
        decoding="async"
        className="absolute inset-0 -z-20 h-full w-full object-cover"
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,rgba(6,20,51,0.15)_0%,rgba(6,20,51,0.35)_35%,rgba(6,20,51,0.86)_68%,rgba(6,20,51,0.97)_100%)]"
      />
      <div className={cn('flex flex-col p-4', size === 'lg' ? 'min-h-[20rem]' : 'min-h-[13.5rem]')}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {badge && <LiveBadge kind={badge} />}
            {chip && <GlassChip>{chip}</GlassChip>}
          </div>
          {corner}
        </div>
        <div className="min-h-6 flex-1" />
        <h3
          className={cn(
            'font-display line-clamp-2 font-extrabold leading-[1.08] tracking-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.45)]',
            size === 'lg' ? 'text-[1.75rem]' : 'text-xl',
          )}
        >
          {title}
        </h3>
        {children}
      </div>
    </article>
  );
}
